import { clientAPISend } from '@1x2network/client_api_integration'
import { flow } from '@1x2network/flow'
import { isPaylineConfiguration } from '@1x2network/is_engine_communication'
import { RestartSetupResponse } from '@1x2network/is_engine_communication/lib/types/response/SetupResponse'
import { slotStore } from '@1x2network/slot-ui'
import { State } from '@1x2network/state_controller'
import { valueIds } from '../../constants/valueIds'
import { BaseGameToHoldAndWinModel, CoinShowerEvent } from '../../model/BaseGameModel'
import { AnyHoldAndWinModel, CollectionEvent, HoldAndWinToBaseModel } from '../../model/HoldAndWinModel'
import { RestartSetupModel, SetupModel } from '../../model/SetupModel'
import { SpinModel } from '../../model/SpinModel'
import { applyHoldAndWinTransition } from '../../utils/applyBoardTransforms'
import { GameViews } from '../../views/GameViews'
import { initWinLines } from './initWinLines'
import { createNudgeEffect } from '@1x2network/tweens'
import { covertToValueSymbols } from '../../model/convertToValueSymbols'

/**
 * Controller for the game in H&W state
 * @author Jordan Harrison - <jordan.harrison@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinState extends State<
    GameViews,
    AnyHoldAndWinModel | BaseGameToHoldAndWinModel,
    RestartSetupResponse<AnyHoldAndWinModel>
> {
    private restarting = false
    private restarted = false

    public prepareState() {
        slotStore.getState().slamStop.setEnabled(false)
    }

    public handshake(model: RestartSetupModel<AnyHoldAndWinModel>): void {
        const {
            excitement,
            hawReelSet,
            reelSet,
            hwBackground,
            baseBackground,
            megaJackpotCounter,
            hawCounter,
            eventManager,
        } = this.views

        eventManager.setEvents(model.results.wins.events)
        reelSet.visible = false
        reelSet.topContainer.visible = false
        hwBackground.visible = true
        baseBackground.visible = false

        excitement.pause()
        excitement.setState('holdandwin')

        const storeState = slotStore.getState()
        storeState.console.setSpinning()
        this.restarting = true
        hawCounter.setRemaining(model.state.holdAndWin.spins.remaining)
        this.views.falsePersistence.setInitialBonus()
        // Mutate the board to apply the hold and win transition and coin shower
        const reels = model.results.board.reels
        const transformEvent = eventManager.peek()?.type === 'TRANSFORM'
        const mutatedSymbols = applyHoldAndWinTransition(
            reels,
            transformEvent ? (eventManager.nextEvent() as CoinShowerEvent) : undefined,
        )
        this.views.hawReelSet.prepareSpin(mutatedSymbols)

        const landedPositions = hawReelSet.fetcher.getPositions(valueIds)
        hawReelSet.lockCells(landedPositions)

        const scatterSymbols = valueIds.flatMap((id) => hawReelSet.fetcher.getSymbolsById(id))
        megaJackpotCounter.setCounter(scatterSymbols.length)
        megaJackpotCounter.visible = true
        if (scatterSymbols.length >= 10) {
            excitement.setMaxHype()
        }

        if (isPaylineConfiguration(model.config)) {
            initWinLines(reelSet, this.views.winLines, model.config.lines)
        }

        // Update jackpot widget values
        this.views.jackpotWidgets.updateValues(model.config.prizes)
    }

    public async introduceState(model: RestartSetupModel<AnyHoldAndWinModel>): Promise<void> {
        this.views.falsePersistence.animateMax()
        await this.handleAllCollectionEvents()
        slotStore.getState().message.setMessage('press-spin-to-continue')
    }

    public inState(model: SpinModel | SetupModel): boolean {
        return (
            model.state.phase.next === 'HOLD_AND_WIN' ||
            (model.state.phase.current === 'HOLD_AND_WIN' && model.state.phase.next === '')
        )
    }

    public async waitForInput(interact: Promise<void>): Promise<object | void> {
        if (this.restarting) {
            const storeState = slotStore.getState()
            storeState.console.setRestart()
            await interact
            storeState.message.clearMessage()
            this.restarting = false
            storeState.console.setSpinning()
            this.restarted = true
        }
    }

    public async waitForRound() {
        if (this.restarted) {
            clientAPISend.feature.start()
            this.restarted = false
        }
        clientAPISend.play.start()
        await Promise.all([this.views.hawCounter.decrease(), this.views.hawReelSet.startSpin()])
    }

    public async leaveState(model: HoldAndWinToBaseModel): Promise<void> {
        const isCapped = model.state.win.isCapped
        const { soundPlayer, hawReelSet, hwBackground } = this.views

        if (!isCapped) {
            await this.views.holdAndWinCountUp.animate(model.results.board.reels)
        }

        await this.handlePopup(model)

        if (isCapped) {
            slotStore.getState().message.setMessage('max-win')
            clientAPISend.actionRequired(true)
            await this.views.maxWinPopup.animate(model.state.win.total.currency)
            clientAPISend.actionRequired(false)
        }
        soundPlayer.fadeBackground(0.3)
        flow.wait(6000).then(() => soundPlayer.fadeBackground(1))
        soundPlayer.playTransitionIn()
        soundPlayer.playHawEnd()
        await this.views.transition.transitionIn()
        hawReelSet.visible = false
        hwBackground.visible = false
        hawReelSet.getTopContainer(0).visible = false
        hawReelSet.getTopContainer(1).visible = false
        hawReelSet.getTopContainer(2).visible = false
        this.views.megaJackpotCounter.visible = false

        // we are transitioning to base game remove cash values
        this.views.reelSet.prepareSpin(covertToValueSymbols(model.results.board.reels))
    }

    public async enterState(model: BaseGameToHoldAndWinModel): Promise<void> {
        const { hawReelSet, transition, hwBackground, megaJackpotCounter, excitement, soundPlayer } = this.views
        slotStore.getState().console.setSpinning()

        const landedPositions = hawReelSet.fetcher.getPositions(valueIds)
        hwBackground.reset()
        hwBackground.visible = true
        hwBackground.playIdle()
        hawReelSet.lockCells(landedPositions)
        hawReelSet.visible = true
        hawReelSet.getTopContainer(0).visible = true
        hawReelSet.getTopContainer(1).visible = true
        hawReelSet.getTopContainer(2).visible = true

        // Update the mega jackpot counter
        const scatterSymbols = valueIds.flatMap((id) => hawReelSet.fetcher.getSymbolsById(id))
        megaJackpotCounter.setCounter(scatterSymbols.length)
        megaJackpotCounter.visible = true

        excitement.pause()
        excitement.setState('holdandwin')
        if (scatterSymbols.length >= 10) {
            excitement.setMaxHype()
        }

        this.views.hawCounter.setRemaining(model.state.holdAndWin.spins.remaining)

        slotStore.getState().message.setMessage('action-required')
        clientAPISend.actionRequired(true)
        await flow.wait(this.views.clickHandler.onClick)
        clientAPISend.actionRequired(false)

        soundPlayer.playTransitionOut()
        await transition.transitionOut()
        this.views.falsePersistence.animateMax()
        slotStore.getState().message.setMessage('good-luck')

        await this.handleAllCollectionEvents()
    }

    async playRound(model: AnyHoldAndWinModel): Promise<void> {
        const { hawReelSet, eventManager } = this.views
        eventManager.setEvents(model.results.wins.events)

        let valueSymbolLandedThisSpin = false
        let counterResetPromise = Promise.resolve()
        const sub = this.views.hawReelSet.spinAnimator.signals.onLand.connect((index) => {
            const reel = index % 5
            const row = Math.floor(index / 5)
            const symbol = hawReelSet.fetcher.getSymbolByPosition([reel, row])

            if (valueIds.includes(symbol.id)) {
                // Play landing animation
                symbol.playLanding()

                if (!valueSymbolLandedThisSpin) {
                    counterResetPromise = this.views.hawCounter.animateChange(model.state.holdAndWin.spins.remaining)
                }
                valueSymbolLandedThisSpin = true

                // Update the mega jackpot counter
                this.views.megaJackpotCounter.onCashSymbolLand()

                if (this.views.megaJackpotCounter.value >= 10) {
                    this.views.excitement.setMaxHype()
                }
                // Nudge the reel
                this.views.effectManager.addAnimation(
                    hawReelSet,
                    createNudgeEffect({ targetOffset: { y: 3 }, duration: 0.18 }),
                )
            }
        })

        await hawReelSet.stopSpin({
            symbols: model.results.board.reels,
            minDuration: 1500,
        })

        sub.disconnect()
        const landedPositions = hawReelSet.fetcher.getPositions(valueIds)
        hawReelSet.lockCells(landedPositions)

        if (!valueSymbolLandedThisSpin) {
            counterResetPromise = this.views.hawCounter.animateChange(model.state.holdAndWin.spins.remaining)
        }

        if (!eventManager.peek()) {
            await flow.wait(500)
        }
        await Promise.all([this.handleAllCollectionEvents(), counterResetPromise])
        clientAPISend.play.end()
    }

    private async handleAllCollectionEvents() {
        const { eventManager } = this.views
        while (eventManager.peek()?.type === 'CASH_COLLECTION') {
            await this.handleCollectionEvent(eventManager.nextEvent() as CollectionEvent)
        }
    }

    private async handleCollectionEvent(event: CollectionEvent): Promise<void> {
        await this.views.collection.animate(event)
    }

    /** animate one of the popups based on win */
    async handlePopup(model: HoldAndWinToBaseModel) {
        const win = model.state.win.total.currency
        const stake = model.bet.stake.currency
        await this.views.tieredPopup.animate(stake, win)
    }
}
